import BackendService from 'BackendService';
import { BackgroundJob, BackgroundJobSummary, JobLinkKey } from 'BackgroundJob';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatHistoryStorage } from '../../utils/ChatHistoryStorage';

export interface JobContextType {
  jobs: BackgroundJobSummary | null;
  loading: boolean;
  refreshJobs: () => void;
  dismissJob: (job: BackgroundJob) => Promise<void>;
  navigateToJob: (job: BackgroundJob) => void;
  jobResultLink: (job: BackgroundJob) => string | undefined;
  notifyNewJob: (jobId: string) => void;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<BackgroundJobSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch all jobs from the new endpoint
  const fetchAllJobs = useCallback(async () => {
    try {
      const response = await new BackendService().getAllJobs();

      // Update jobs state
      const runningJobs = response.filter(
        (j) => j.status === 'IN_PROGRESS' || j.status === 'PENDING',
      );
      const failedJobs = response.filter((j) => j.status === 'FAILED');
      const completedJobs = response.filter((j) => j.status === 'COMPLETED');

      setJobs({
        runningJobs,
        failedJobs,
        recentlyCompletedJobs: completedJobs,
        totalRunning: runningJobs.length,
        totalFailed: failedJobs.length,
      });

      setLoading(false);

      // No longer honoring polling suggestions - using fixed interval only
    } catch (error) {
      console.error('Failed to fetch all jobs:', error);
      setLoading(false);
    }
  }, []);

  const refreshJobs = useCallback(() => {
    fetchAllJobs();
  }, [fetchAllJobs]);

  // Handle job creation notifications from UI components
  const notifyNewJob = useCallback(
    async (jobId: string) => {
      console.log('JobContext received notification for new job:', jobId);

      // Refresh the job list to get the new job with polling suggestions
      await fetchAllJobs();
    },
    [fetchAllJobs],
  );

  const dismissJob = useCallback(
    async (job: BackgroundJob): Promise<void> => {
      try {
        await new BackendService().dismissBackgroundJob(job.id);
        refreshJobs();
      } catch (error) {
        console.error('Failed to dismiss job:', error);
        throw error;
      }
    },
    [refreshJobs],
  );

  const jobResultLink = (job: BackgroundJob): string | undefined => {
    if (!job.linkKey || !job.linkedModelId) {
      return;
    }

    switch (job.linkKey as JobLinkKey) {
      case 'ARTICLE_REFINEMENT':
        return `/articles/${job.linkedModelId}`;
      case 'FUND_OPPORTUNITY_CREATION':
        return `/opportunities/${job.linkedModelId}`;
      case 'PDF_GENERATION':
        console.info('PDF generation job clicked - no navigation configured');
        return;
      default:
        console.warn('Unknown job link key:', job.linkKey);
        return;
    }
  };

  const navigateToJob = useCallback(
    (job: BackgroundJob) => {
      const targetUrl = jobResultLink(job);

      if (!targetUrl) {
        return;
      }

      // Use React Router's navigate function for proper navigation
      navigate(targetUrl);
    },
    [navigate],
  );

  useEffect(() => {
    // Cleanup expired chat histories on initialization
    ChatHistoryStorage.cleanupExpiredHistories();

    // Initial load
    refreshJobs();

    // Fallback polling every 10 minutes maximum (in case no jobs need polling)
    const fallbackInterval = setInterval(() => {
      fetchAllJobs();
    }, 600000); // 10 minutes

    return () => {
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
  }, [refreshJobs, fetchAllJobs]);

  const value: JobContextType = {
    jobs,
    loading,
    refreshJobs,
    dismissJob,
    navigateToJob,
    jobResultLink,
    notifyNewJob,
  };

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

export const useJobs = (): JobContextType => {
  const context = useContext(JobContext);
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};
